import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  GraduationCap,
  Send,
  Sparkles,
  Loader2,
  MessageCircleQuestion,
  HandHelping,
  ClipboardCheck,
  RotateCcw,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { streamSSE } from "@/lib/streamSSE";
import { useAuth } from "@/hooks/useAuth";
import { useMasteryJourney } from "@/hooks/useMasteryJourney";
import { getTutorContext, subscribeTutorContext, type TutorContext } from "@/lib/tutorContextBus";
import { motionScrollBehavior } from "@/lib/motion";
import { toast } from "@/hooks/use-toast";
import { getTutorSubjectsForPath } from "@/data/tutorSubjects";

type Msg = { role: "user" | "assistant"; content: string };

const TUTOR_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/tutor-chat`;
// Client-side cap. The server enforces a stricter per-IP+UA cap on top of
// this, so even if a guest clears localStorage they hit the server wall.
const GUEST_QUESTION_LIMIT = 3;
const STORAGE_GUEST_COUNT = "hl.tutor.guest.count";
const STORAGE_GUEST_TOKEN = "hl.tutor.guest.token";
const STORAGE_GUEST_FIRST = "hl.tutor.guest.first";

// Per-thread persistence. We key by user (or guest token) + a "thread id"
// derived from the active playbook, falling back to the page pathname when
// no playbook is in scope. Each thread caps at the most recent ~30 turns
// to keep localStorage small (and to avoid resending huge histories).
const STORAGE_THREAD_PREFIX = "hl.tutor.thread.";
const MAX_PERSISTED_TURNS = 30;

function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60) || "default";
}

/**
 * Build the localStorage key for a tutor thread. Guests get keyed by their
 * stable browser token (set up by getGuestToken below) so wiping
 * localStorage starts fresh, but reopening the tutor mid-session restores.
 * Authed users get keyed by user.id so the same thread follows them across
 * tabs in the same browser.
 */
function makeThreadKey(opts: {
  userId?: string;
  guestToken?: string;
  playbookTitle?: string;
  pathname: string;
}): string {
  const owner = opts.userId
    ? `u:${opts.userId}`
    : `g:${opts.guestToken || "anon"}`;
  const scope = opts.playbookTitle
    ? `pb:${slugify(opts.playbookTitle)}`
    : `path:${slugify(opts.pathname)}`;
  return `${STORAGE_THREAD_PREFIX}${owner}|${scope}`;
}

function loadThread(key: string): Msg[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (m): m is Msg =>
          !!m &&
          typeof m === "object" &&
          (m as Msg).role !== undefined &&
          typeof (m as Msg).content === "string",
      )
      .slice(-MAX_PERSISTED_TURNS);
  } catch {
    return [];
  }
}

function saveThread(key: string, messages: Msg[]) {
  if (typeof window === "undefined") return;
  try {
    if (messages.length === 0) {
      window.localStorage.removeItem(key);
      return;
    }
    const trimmed = messages.slice(-MAX_PERSISTED_TURNS);
    window.localStorage.setItem(key, JSON.stringify(trimmed));
  } catch {
    /* ignore quota errors */
  }
}

/**
 * Get-or-create a stable per-browser guest token. We send this on every
 * tutor request so the server can key its rolling 24h cap on
 * (ip + ua + token). Wiping localStorage rotates the token but the IP+UA
 * portion of the key still holds the cap in place for 24h.
 */
function getGuestToken(): string {
  if (typeof window === "undefined") return "";
  try {
    let t = window.localStorage.getItem(STORAGE_GUEST_TOKEN);
    if (!t) {
      // crypto.randomUUID is available in all evergreen browsers we target.
      t = (globalThis.crypto?.randomUUID?.() ?? `g-${Date.now().toString(36)}`)
        .replace(/-/g, "")
        .slice(0, 24);
      window.localStorage.setItem(STORAGE_GUEST_TOKEN, t);
      window.localStorage.setItem(STORAGE_GUEST_FIRST, String(Date.now()));
    }
    return t;
  } catch {
    return "";
  }
}

// Always-on quick actions — small, action-oriented, page-agnostic. The
// richer "popular subjects" list comes from getTutorSubjectsForPath and
// only shows before the user starts a conversation (to avoid clutter).
const QUICK_PROMPTS: { label: string; prompt: string }[] = [
  { label: "What do I do next?", prompt: "What's the single best next action I can take right now on this page?" },
  { label: "Teach me this concept", prompt: "Give me a 3-bullet primer on the core concept I'm working with right now." },
  { label: "Why this model?", prompt: "Why would I pick the current model for this task, and when should I switch?" },
];

/**
 * Build a structured prompt for the "Do it with me" / "Check my work" flows.
 * We intentionally inline the page + playbook + step + (for review) the
 * most recent assistant output so the tutor doesn't have to guess what
 * the user is looking at — it can respond with concrete, on-rails guidance.
 */
function buildStructuredPrompt(
  intent: "walk-through" | "review",
  ctx: TutorContext,
  pathname: string,
): string {
  const lines: string[] = [];
  lines.push(
    intent === "walk-through"
      ? "I want you to walk me through the current step with me, doing it together."
      : "I just produced something — please check my work and tell me what to fix.",
  );
  lines.push("");
  lines.push("Context:");
  lines.push(`- Page: ${pathname}`);
  if (ctx.currentPlaybook) lines.push(`- Playbook: ${ctx.currentPlaybook}`);
  if (ctx.currentStep) lines.push(`- Step: ${ctx.currentStep}`);
  if (ctx.currentModel) lines.push(`- Model in use: ${ctx.currentModel}`);

  if (intent === "review") {
    const lastAssistant = [...(ctx.recentMessages ?? [])]
      .reverse()
      .find((m) => m.role === "assistant" && m.content.trim().length > 0);
    if (lastAssistant) {
      lines.push("");
      lines.push("Most recent output to review:");
      lines.push("```");
      lines.push(lastAssistant.content.slice(0, 800));
      lines.push("```");
    }
    lines.push("");
    lines.push(
      "Score it 1-5, list the top 2 things to fix, and give one concrete improvement I can paste back in.",
    );
  } else {
    lines.push("");
    lines.push(
      "Give me: (1) the exact next action in one sentence, (2) a starter prompt I can use right now, (3) what 'done well' looks like.",
    );
  }
  return lines.join("\n");
}

interface TutorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function TutorSheet({ open, onOpenChange }: TutorSheetProps) {
  const { user } = useAuth();
  const location = useLocation();
  const journey = useMasteryJourney();
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [ctx, setCtx] = useState<TutorContext>(() => getTutorContext());
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Track whether we've already hydrated the current thread key, so that
  // (a) we don't write an empty array over a real thread before load runs
  // and (b) switching playbooks correctly swaps in the right history.
  const hydratedKeyRef = useRef<string | null>(null);

  // Keep tutor context fresh while the sheet is open.
  useEffect(() => subscribeTutorContext(setCtx), []);

  // Compute the storage key for the active tutor thread. Recomputes when
  // the user signs in/out or the active playbook/page changes, so each
  // playbook keeps its own history.
  const threadKey = useMemo(
    () =>
      makeThreadKey({
        userId: user?.id,
        guestToken: !user ? getGuestToken() : undefined,
        playbookTitle: ctx.currentPlaybook,
        pathname: location.pathname,
      }),
    [user?.id, ctx.currentPlaybook, location.pathname],
  );

  // Hydrate the transcript whenever the thread key changes (e.g. user
  // navigates from one playbook to another, or signs in). Mark the key as
  // hydrated so the persistence effect below knows it can safely write.
  useEffect(() => {
    const restored = loadThread(threadKey);
    setMessages(restored);
    hydratedKeyRef.current = threadKey;
  }, [threadKey]);

  // Persist the transcript on any change, but only after we've hydrated
  // the matching key — otherwise the initial render would overwrite a
  // saved thread with the empty default state.
  useEffect(() => {
    if (hydratedKeyRef.current !== threadKey) return;
    saveThread(threadKey, messages);
  }, [messages, threadKey]);

  // Auto-scroll to the latest message.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: motionScrollBehavior() });
  }, [messages, streaming]);

  const guestCount = useMemo(() => {
    if (user) return 0;
    if (typeof window === "undefined") return 0;
    try {
      return Number(window.localStorage.getItem(STORAGE_GUEST_COUNT) || "0") || 0;
    } catch {
      return 0;
    }
  }, [user, messages.length]);

  const guestBlocked = !user && guestCount >= GUEST_QUESTION_LIMIT;

  // Contextual "popular subjects past learners get stuck on" — picked by
  // the current pathname so chips stay relevant as the user navigates.
  const subjectBucket = useMemo(
    () => getTutorSubjectsForPath(location.pathname),
    [location.pathname],
  );

  // User-initiated "start fresh" — wipes only the current thread, not all
  // tutor state. Useful when a topic shifts and the model keeps anchoring
  // on stale earlier turns.
  const resetThread = useCallback(() => {
    setMessages([]);
    saveThread(threadKey, []);
    setInput("");
  }, [threadKey]);

  const ask = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || streaming) return;

      if (guestBlocked) {
        toast({
          title: "Sign up to keep asking",
          description: `Guests get ${GUEST_QUESTION_LIMIT} free tutor questions per day. Create a free account to unlock unlimited tutor help.`,
        });
        return;
      }

      const userMsg: Msg = { role: "user", content: trimmed };
      const nextMessages = [...messages, userMsg];
      setMessages(nextMessages);
      setInput("");
      setStreaming(true);

      // Optimistically append an empty assistant turn we'll fill as tokens arrive.
      setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

      let assistantSoFar = "";
      try {
        await streamSSE({
          url: TUTOR_URL,
          body: {
            messages: nextMessages,
            context: {
              page: location.pathname,
              currentPlaybook: ctx.currentPlaybook,
              currentStep: ctx.currentStep,
              currentModel: ctx.currentModel,
              masteryProgress: ctx.masteryProgress ?? {
                launched: journey.journey.launchedSkills.length,
                total: 100,
              },
              // Prefer the surrounding chat-shell transcript when available
              // (it's what the user is actually working on); otherwise fall
              // back to the tutor's own short conversation history so the
              // model still sees follow-up questions in context.
              recentMessages:
                ctx.recentMessages && ctx.recentMessages.length > 0
                  ? ctx.recentMessages
                  : nextMessages.slice(-4),
            },
          },
          extraHeaders: !user
            ? {
                "X-HL-Guest": "1",
                "X-HL-Guest-Token": getGuestToken(),
              }
            : undefined,
          onDelta: (chunk) => {
            assistantSoFar += chunk;
            setMessages((prev) =>
              prev.map((m, i) =>
                i === prev.length - 1 && m.role === "assistant" ? { ...m, content: assistantSoFar } : m,
              ),
            );
          },
        });

        // Bump guest count only after a successful exchange.
        if (!user && typeof window !== "undefined") {
          try {
            window.localStorage.setItem(STORAGE_GUEST_COUNT, String(guestCount + 1));
          } catch {
            /* ignore */
          }
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Tutor failed to respond";
        const status = (err as { status?: number } | undefined)?.status;
        // If the server hit its guest cap, force the local counter up so
        // the upgrade prompt UI shows immediately even if localStorage
        // had been reset.
        if (status === 429 && /guest/i.test(message) && !user && typeof window !== "undefined") {
          try {
            window.localStorage.setItem(STORAGE_GUEST_COUNT, String(GUEST_QUESTION_LIMIT));
          } catch {
            /* ignore */
          }
        }
        setMessages((prev) => prev.slice(0, -1));
        toast({ title: "Tutor error", description: message, variant: "destructive" });
      } finally {
        setStreaming(false);
      }
    },
    [ctx, guestBlocked, guestCount, journey.journey.launchedSkills.length, location.pathname, messages, streaming, user],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void ask(input);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <SheetTitle className="flex items-center gap-2 text-base">
              <span className="grid h-8 w-8 place-items-center rounded-md bg-primary/10 text-primary">
                <GraduationCap className="h-4 w-4" />
              </span>
              <span className="flex flex-col text-left">
                <span>Tutor</span>
                <span className="text-xs font-normal text-muted-foreground">
                  {ctx.currentPlaybook
                    ? `Helping with: ${ctx.currentPlaybook}`
                    : "Ask anything about what you're doing"}
                </span>
              </span>
            </SheetTitle>
            {messages.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={resetThread}
                disabled={streaming}
                className="h-8 gap-1 px-2 text-xs text-muted-foreground hover:text-foreground"
                title="Start a fresh tutor thread for this context"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                New thread
              </Button>
            )}
          </div>
        </SheetHeader>

        {/* Structured action buttons — fire a richly-contextualized
            request based on the current page + playbook + step. */}
        <div className="grid grid-cols-2 gap-2 border-b bg-muted/30 px-5 py-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={streaming || guestBlocked}
            onClick={() =>
              void ask(buildStructuredPrompt("walk-through", ctx, location.pathname))
            }
            className="justify-start gap-2"
          >
            <HandHelping className="h-4 w-4 text-primary" />
            <span className="text-xs font-medium">Do it with me</span>
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={streaming || guestBlocked}
            onClick={() =>
              void ask(buildStructuredPrompt("review", ctx, location.pathname))
            }
            className="justify-start gap-2"
          >
            <ClipboardCheck className="h-4 w-4 text-primary" />
            <span className="text-xs font-medium">Check my work</span>
          </Button>
        </div>

        {/* Quick-prompt chips */}
        <div className="flex flex-wrap gap-2 border-b bg-muted/40 px-5 py-3">
          {QUICK_PROMPTS.map((qp) => (
            <button
              key={qp.label}
              type="button"
              disabled={streaming || guestBlocked}
              onClick={() => void ask(qp.prompt)}
              className={cn(
                "rounded-full border bg-background px-3 py-1 text-xs font-medium transition-colors",
                "hover:border-primary/60 hover:text-primary",
                "disabled:opacity-50",
              )}
            >
              <Sparkles className="mr-1 inline h-3 w-3" />
              {qp.label}
            </button>
          ))}
        </div>

        {/* Transcript */}
        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {messages.length === 0 && (
            <div className="space-y-4">
              <div className="rounded-md border border-dashed bg-muted/40 p-4 text-sm text-muted-foreground">
                Hi! I'm your tutor. Tap a subject below or ask anything about what
                you're working on. I'll keep answers short and suggest one next step.
              </div>

              <div className="rounded-md border bg-card p-3">
                <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <MessageCircleQuestion className="h-3.5 w-3.5" />
                  {subjectBucket.heading}
                </div>
                <ul className="space-y-1.5">
                  {subjectBucket.subjects.map((s) => (
                    <li key={s.label}>
                      <button
                        type="button"
                        disabled={streaming || guestBlocked}
                        onClick={() => void ask(s.prompt)}
                        className={cn(
                          "w-full rounded-md border bg-background px-3 py-2 text-left text-sm",
                          "transition-colors hover:border-primary/60 hover:bg-primary/5 hover:text-primary",
                          "disabled:opacity-50",
                        )}
                      >
                        {s.label}
                      </button>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Curated from common questions other learners ask.
                </p>
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div
              key={i}
              className={cn(
                "rounded-md px-3 py-2 text-sm leading-relaxed",
                m.role === "user"
                  ? "ml-auto max-w-[85%] bg-primary/10 text-foreground"
                  : "mr-auto max-w-[90%] bg-muted",
              )}
            >
              {m.content || (streaming && i === messages.length - 1 ? (
                <span className="inline-flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> Thinking…
                </span>
              ) : null)}
            </div>
          ))}
        </div>

        {/* Composer */}
        <form onSubmit={handleSubmit} className="border-t bg-background p-3">
          {guestBlocked ? (
            <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
              <p className="font-medium text-foreground">Tutor unlocked with a free account</p>
              <p className="mt-1 text-xs text-muted-foreground">
                You've used your {GUEST_QUESTION_LIMIT} free tutor questions. Sign up to keep asking — it's free.
              </p>
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void ask(input);
                  }
                }}
                placeholder="Ask the tutor…"
                rows={2}
                disabled={streaming}
                className="min-h-[48px] flex-1 resize-none text-sm"
              />
              <Button type="submit" size="icon" disabled={streaming || !input.trim()}>
                {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
          )}
          {!user && !guestBlocked && (
            <p className="mt-2 text-[11px] text-muted-foreground">
              Guest mode · {GUEST_QUESTION_LIMIT - guestCount} free question
              {GUEST_QUESTION_LIMIT - guestCount === 1 ? "" : "s"} left
            </p>
          )}
        </form>
      </SheetContent>
    </Sheet>
  );
}